// src/components/Finanzas/MiHistorialBolsillo.jsx
// Historial del bolsillo: lista todas las relaciones de gastos cerradas del
// usuario y, al expandir cada una, muestra los gastos, entregas y traslados
// que quedaron capturados en ese cierre. Carga detalle on-demand (lazy) para
// no traer todo el dataset de una vez.
//
// Esta vista DESCARGA /MiBolsa de items historicos: alli solo viven los
// items del periodo abierto (no incluidos aun en relacion APROBADA+).

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Accordion, Table, Badge, Alert, Spinner, Button
} from 'react-bootstrap';
import { FaHistory, FaArrowLeft, FaFileInvoiceDollar, FaArrowDown, FaArrowUp, FaExchangeAlt, FaFilePdf } from 'react-icons/fa';
import { Alertassw } from '../Alertas/Alertassw';
import { listarRelaciones, listarGastos, listarEntregas, listarTraslados } from '../../api/finanzas';
import { useAuth } from '../../hooks/useAuth';
import { extractError } from './utils/extractError';
import RelacionDetalleContenido from './RelacionDetalleContenido';
import { descargarPdfRelacionLocal } from './utils/descargarPdfHelper';

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const ESTADOS_CERRADOS = ['APROBADA', 'SINCRONIZANDO', 'SINCRONIZADA', 'SYNC_ERROR'];

const ESTADO_BADGE = {
  APROBADA: 'info',
  SINCRONIZANDO: 'info',
  SINCRONIZADA: 'success',
  SYNC_ERROR: 'danger'
};

const MiHistorialBolsillo = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();

  const [relaciones, setRelaciones] = useState([]);
  // Cache por relacionId: { loaded, loading, gastos, entregas, traslados, error }
  const [detalles, setDetalles] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const resp = await listarRelaciones({
        conditions: {
          responsable: userData._id,
          estado: { $in: ESTADOS_CERRADOS },
          activo: true
        },
        sort: { fechaAprobacion: -1, createdAt: -1 }
      });
      if (resp?.status === 200) {
        setRelaciones(resp.data.data || []);
      }
    } catch (err) {
      console.error('Error cargar historial:', err);
      Alertassw('error', 'Error', extractError(err, 'No se pudo cargar el historial'));
    } finally {
      setLoading(false);
    }
  };

  // Lazy load del detalle al abrir cada accordion. La relacion ya tiene los
  // arrays de IDs (gastosIncluidos, entregasIncluidas, trasladosIncluidos)
  // desde la consulta inicial; aqui los hidratamos batch via los listar*.
  const handleAccordionSelect = (eventKey) => {
    if (!eventKey) return;
    const relacionId = eventKey;
    if (detalles[relacionId]?.loaded || detalles[relacionId]?.loading) return;
    const relacion = relaciones.find(r => r._id === relacionId);
    if (!relacion) return;
    cargarDetalle(relacion);
  };

  const cargarDetalle = async (relacion) => {
    const relacionId = relacion._id;
    setDetalles(prev => ({ ...prev, [relacionId]: { loading: true } }));
    try {
      const gastosIds    = relacion.gastosIncluidos    || [];
      const entregasIds  = relacion.entregasIncluidas  || [];
      const trasladosIds = relacion.trasladosIncluidos || [];

      const [gastosResp, entregasResp, trasladosResp] = await Promise.all([
        gastosIds.length
          ? listarGastos({ conditions: { _id: { $in: gastosIds } }, sort: { fecha: -1 } })
          : Promise.resolve({ status: 200, data: { data: [] } }),
        entregasIds.length
          ? listarEntregas({ conditions: { _id: { $in: entregasIds } }, sort: { fecha: -1 } })
          : Promise.resolve({ status: 200, data: { data: [] } }),
        trasladosIds.length
          ? listarTraslados({ conditions: { _id: { $in: trasladosIds } }, sort: { fecha: -1 } })
          : Promise.resolve({ status: 200, data: { data: [] } })
      ]);

      const bolsilloId = String(relacion.bolsillo?._id || relacion.bolsillo);
      setDetalles(prev => ({
        ...prev,
        [relacionId]: {
          loaded: true,
          loading: false,
          bolsilloId,
          gastos:    gastosResp?.data?.data    || [],
          entregas:  entregasResp?.data?.data  || [],
          traslados: trasladosResp?.data?.data || []
        }
      }));
    } catch (err) {
      console.error('Error cargar detalle relacion:', err);
      setDetalles(prev => ({
        ...prev,
        [relacionId]: { loading: false, error: extractError(err, 'No se pudo cargar el detalle') }
      }));
    }
  };

  // La presentacion completa del detalle (saldos + gastos + movimientos)
  // vive en RelacionDetalleContenido, compartido con MisRelacionesGastos.jsx
  // y DetalleRelacion.jsx. Aqui solo se consume con la data ya hidratada.

  if (loading) {
    return (
      <Container fluid className="py-4 w-100">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Cargando historial del bolsillo...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 w-100">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="text-primary fw-bold mb-1">
            <FaHistory className="me-2" />
            Historial de mi Bolsa
          </h2>
          <p className="text-muted mb-0">
            Cierres realizados (relaciones APROBADAS o SINCRONIZADAS) con sus gastos y movimientos.
          </p>
        </Col>
        <Col xs="auto">
          <Button variant="outline-secondary" onClick={() => navigate('/MiBolsa')}>
            <FaArrowLeft className="me-1" /> Volver a Mi Bolsa
          </Button>
        </Col>
      </Row>

      {relaciones.length === 0 ? (
        <Alert variant="info" className="text-center">
          <FaFileInvoiceDollar size={40} className="mb-2 text-primary" />
          <h5 className="mb-1">Aún no tienes cierres aprobados</h5>
          <p className="mb-0 small">
            Cuando cierres y apruebes una relación de gastos en{' '}
            <a href="/MiBolsa" className="alert-link">Mi Bolsa</a>, aparecerá aquí.
          </p>
        </Alert>
      ) : (
        <Card className="shadow-sm w-100">
          <Card.Body className="w-100">
            <Accordion onSelect={handleAccordionSelect}>
              {relaciones.map(r => {
                const detalle = detalles[r._id];
                return (
                  <Accordion.Item eventKey={r._id} key={r._id}>
                    <Accordion.Header>
                      <div className="d-flex justify-content-between w-100 me-3 align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <strong>{r.consecutivo}</strong>
                          <Badge bg={ESTADO_BADGE[r.estado] || 'secondary'}>{r.estado}</Badge>
                          {r.tipo === 'CORRECCION' && (
                            <Badge bg="dark">CORRECCIÓN</Badge>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <small className="text-muted">
                            {r.fechaAprobacion
                              ? new Date(r.fechaAprobacion).toLocaleDateString('es-CO')
                              : (r.fechaCierre ? new Date(r.fechaCierre).toLocaleDateString('es-CO') : '—')}
                          </small>
                          <strong className="text-primary">{formatoCOP(r.montoTotalGastos)}</strong>
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <RelacionDetalleContenido
                        relacion={r}
                        gastos={detalle?.gastos || []}
                        entregas={detalle?.entregas || []}
                        traslados={detalle?.traslados || []}
                        bolsilloId={detalle?.bolsilloId}
                        cargandoDetalle={detalle?.loading}
                        errorDetalle={detalle?.error}
                        onReintentar={() => cargarDetalle(r)}
                      />

                      <div className="mt-3 d-flex gap-2 flex-wrap">
                        <Button variant="outline-primary" size="sm"
                          onClick={() => navigate(`/RevisionCierre/${r._id}`)}>
                          Ver vista de revisión completa →
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={(e) => {
                            // stopPropagation no es necesario aqui (estamos en el body
                            // del accordion, no en el header), pero lo dejamos para
                            // mantener consistencia si en el futuro el handler escala.
                            e.stopPropagation();
                            descargarPdfRelacionLocal({ relacion: r });
                          }}
                        >
                          <FaFilePdf className="me-1" /> Descargar PDF
                        </Button>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default MiHistorialBolsillo;
